import React, { useState, useEffect } from 'react';
import Layout from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { useToast } from '../hooks/use-toast';
import {
  Calendar,
  MapPin,
  Clock,
  Users,
  Video,
  Building,
  Filter,
  Loader2,
  QrCode,
  CheckCircle,
  XCircle
} from 'lucide-react';

const CalendarioPage = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('all');
  const [registrations, setRegistrations] = useState({});
  const [registering, setRegistering] = useState(null);
  const { user, isAuthenticated } = useAuth();
  const { toast } = useToast();

  useEffect(() => {
    fetchEvents();
  }, []);

  useEffect(() => {
    if (isAuthenticated && events.length > 0) {
      checkRegistrations();
    }
  }, [isAuthenticated, events]);

  const fetchEvents = async () => {
    try {
      const response = await api.get('/events');
      setEvents(response.data);
    } catch (error) {
      console.error('Error fetching events:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkRegistrations = async () => {
    const regs = {};
    for (const event of events) {
      try {
        const response = await api.get(`/events/${event.id}/my-registration`);
        if (response.data.registered) {
          regs[event.id] = response.data;
        }
      } catch (error) {
        // Not registered
      }
    }
    setRegistrations(regs);
  };

  const handleRegister = async (eventId) => {
    if (!isAuthenticated) {
      toast({
        title: "Inicia sesión",
        description: "Debes iniciar sesión para registrarte a eventos",
        variant: "destructive"
      });
      return;
    }

    setRegistering(eventId);
    try {
      const response = await api.post(`/events/${eventId}/register`);
      setRegistrations(prev => ({
        ...prev,
        [eventId]: {
          registered: true,
          qr_code: response.data.qr_code,
          qr_data: response.data.qr_data
        }
      }));
      toast({
        title: "¡Registro exitoso!",
        description: "Te hemos registrado al evento. Revisa tu código QR.",
      });
      fetchEvents(); // Refresh to update spots
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "No se pudo completar el registro",
        variant: "destructive"
      });
    } finally {
      setRegistering(null);
    }
  };

  const handleCancelRegistration = async (eventId) => {
    try {
      await api.delete(`/events/${eventId}/register`);
      setRegistrations(prev => {
        const newRegs = {...prev};
        delete newRegs[eventId];
        return newRegs;
      });
      toast({
        title: "Registro cancelado",
        description: "Tu registro ha sido cancelado",
      });
      fetchEvents();
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "No se pudo cancelar el registro",
        variant: "destructive"
      });
    }
  };

  const filteredEvents = events.filter(event => {
    if (filterType === 'all') return true;
    return event.event_type === filterType;
  });

  const formatDate = (dateString) => {
    if (!dateString) return 'Fecha por confirmar';
    return new Date(dateString).toLocaleDateString('es-MX', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative py-16 lg:py-20 bg-gradient-to-br from-gray-100 via-gray-50 to-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <Badge className="bg-gray-200 text-gray-700 mb-4">Eventos UPIM</Badge>
            <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
              Calendario de Eventos
            </h1>
            <p className="text-lg text-gray-600">
              Capacitaciones, networking y eventos exclusivos para miembros UPIM
            </p>
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="py-6 border-b bg-white sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center space-x-4">
            <Filter className="h-5 w-5 text-gray-500" />
            <span className="text-sm text-gray-500">Filtrar:</span>
            <div className="flex space-x-2">
              {[
                { value: 'all', label: 'Todos' },
                { value: 'presencial', label: 'Presencial' },
                { value: 'online', label: 'Online' }
              ].map(filter => (
                <Button
                  key={filter.value}
                  variant={filterType === filter.value ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFilterType(filter.value)}
                  className={filterType === filter.value ? 'bg-gray-900' : ''}
                >
                  {filter.label}
                </Button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Events List */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
            </div>
          ) : filteredEvents.length === 0 ? (
            <div className="text-center py-20">
              <Calendar className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No hay eventos programados</p>
            </div>
          ) : (
            <div className="space-y-6">
              {filteredEvents.map(event => (
                <Card key={event.id} className="overflow-hidden hover:shadow-lg transition-shadow">
                  <CardContent className="p-0">
                    <div className="flex flex-col md:flex-row">
                      {/* Event Image */}
                      <div className="md:w-64 h-48 md:h-auto flex-shrink-0 bg-gray-200">
                        {event.image_url ? (
                          <img 
                            src={event.image_url} 
                            alt={event.title}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Calendar className="h-12 w-12 text-gray-400" />
                          </div>
                        )}
                      </div>

                      {/* Event Info */}
                      <div className="flex-1 p-6">
                        <div className="flex flex-wrap items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-2">
                              <Badge className={event.event_type === 'online' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}>
                                {event.event_type === 'online' ? (
                                  <><Video className="h-3 w-3 mr-1" /> Online</>
                                ) : (
                                  <><Building className="h-3 w-3 mr-1" /> Presencial</>
                                )}
                              </Badge>
                              {event.price === 0 && (
                                <Badge className="bg-gray-100 text-gray-700">Gratis</Badge>
                              )}
                            </div>
                            
                            <h3 className="text-xl font-bold text-gray-900 mb-2">{event.title}</h3>
                            <p className="text-gray-600 text-sm mb-4">{event.description}</p>

                            <div className="space-y-2 text-sm text-gray-500">
                              <div className="flex items-center">
                                <Calendar className="h-4 w-4 mr-2 text-gray-400" />
                                {formatDate(event.date)}
                              </div>
                              {event.time && (
                                <div className="flex items-center">
                                  <Clock className="h-4 w-4 mr-2 text-gray-400" />
                                  {event.time} hrs
                                </div>
                              )}
                              {event.location && (
                                <div className="flex items-center">
                                  <MapPin className="h-4 w-4 mr-2 text-gray-400" />
                                  {event.location}
                                </div>
                              )}
                              {event.total_spots && (
                                <div className="flex items-center">
                                  <Users className="h-4 w-4 mr-2 text-gray-400" />
                                  {event.available_spots} lugares disponibles de {event.total_spots}
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Registration Section */}
                          <div className="w-full md:w-auto flex flex-col items-center space-y-3">
                            {event.price > 0 && (
                              <div className="text-center">
                                <p className="text-2xl font-bold text-gray-900">${event.price.toLocaleString()}</p>
                                <p className="text-xs text-gray-500">MXN</p>
                              </div>
                            )}

                            {registrations[event.id] ? (
                              <div className="text-center">
                                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-3">
                                  <CheckCircle className="h-8 w-8 text-green-600 mx-auto mb-2" />
                                  <p className="text-sm font-medium text-green-700">¡Registrado!</p>
                                  {registrations[event.id].qr_code && (
                                    <div className="mt-3">
                                      <img 
                                        src={`data:image/png;base64,${registrations[event.id].qr_code}`}
                                        alt="QR Code"
                                        className="w-24 h-24 mx-auto"
                                      />
                                      <p className="text-xs text-gray-500 mt-1">Tu pase de entrada</p>
                                    </div>
                                  )}
                                </div>
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  className="text-red-600 border-red-200 hover:bg-red-50"
                                  onClick={() => handleCancelRegistration(event.id)}
                                >
                                  <XCircle className="h-4 w-4 mr-1" />
                                  Cancelar
                                </Button>
                              </div>
                            ) : (
                              <Button
                                className="bg-gray-900 hover:bg-gray-800 w-full md:w-auto"
                                onClick={() => handleRegister(event.id)}
                                disabled={registering === event.id || (event.available_spots !== null && event.available_spots <= 0)}
                              >
                                {registering === event.id ? (
                                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Registrando...</>
                                ) : event.available_spots !== null && event.available_spots <= 0 ? (
                                  'Sin lugares'
                                ) : (
                                  <><QrCode className="h-4 w-4 mr-2" /> Registrarme</>
                                )}
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
};

export default CalendarioPage;
